/**
 * DocReviewPreview — file preview via direct download URL + token query param.
 */

import { useEffect, useState } from 'react'
import { resolveApiUrl, getToken } from '../../../api/client'
import type { HubInboxItem } from '../../../api/types'

const IMAGE_EXTS = ['.png', '.jpg', '.jpeg', '.gif', '.webp']
const TEXT_MAX_BYTES = 10 * 1024

type FileKind = 'pdf' | 'image' | 'excel' | 'word' | 'text' | 'other'

function extension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot).toLowerCase() : ''
}

function fileKind(name: string, mime?: string | null): FileKind {
  const ext = extension(name)

  if (ext === '.pdf') return 'pdf'
  if (IMAGE_EXTS.includes(ext)) return 'image'
  if (ext === '.xls' || ext === '.xlsx') return 'excel'
  if (ext === '.doc' || ext === '.docx') return 'word'
  if (ext === '.txt' || ext === '.csv') return 'text'

  if (mime?.includes('pdf')) return 'pdf'
  if (mime?.startsWith('image/')) return 'image'
  if (mime?.includes('spreadsheet') || mime?.includes('excel') || mime?.includes('ms-excel')) return 'excel'
  if (mime?.includes('word') || mime?.includes('msword') || mime?.includes('document')) return 'word'
  if (mime?.startsWith('text/') || mime === 'application/csv') return 'text'

  return 'other'
}

function downloadUrl(projectId: number, uploadId: number): string {
  const token = getToken() ?? ''
  const path = `/api/projects/${projectId}/hub/uploads/${uploadId}/download?token=${encodeURIComponent(token)}`
  const resolved = resolveApiUrl(path)
  if (resolved.startsWith('http://') || resolved.startsWith('https://')) {
    return resolved
  }
  return `${window.location.origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`
}

function DownloadPanel({
  name,
  url,
  icon,
  label,
}: {
  name:  string
  url:   string
  icon:  string
  label?: string
}) {
  return (
    <div className="pm-dr-preview" style={{ textAlign: 'center', padding: '40px 22px' }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>{name}</div>
      {label && (
        <div style={{ fontSize: 11, color: 'var(--ink4)', marginBottom: 18 }}>{label}</div>
      )}
      <a href={url} download={name} className="btn b-dk" style={{ padding: '9px 22px', display: 'inline-block', textDecoration: 'none' }}>
        ↓ Download
      </a>
    </div>
  )
}

function TextPreview({ url, name }: { url: string; name: string }) {
  const [text, setText]       = useState<string | null>(null)
  const [tooLarge, setTooLarge] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setTooLarge(false)
    setText(null)

    fetch(url)
      .then(async res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const len = Number(res.headers.get('content-length') ?? 0)
        if (len > TEXT_MAX_BYTES) {
          setTooLarge(true)
          return
        }
        const body = await res.text()
        if (body.length > TEXT_MAX_BYTES) {
          setTooLarge(true)
          return
        }
        if (!cancelled) setText(body)
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Preview failed')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [url])

  if (loading) {
    return (
      <div className="pm-dr-preview" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 12, color: 'var(--ink4)' }}>Loading preview…</div>
      </div>
    )
  }

  if (tooLarge) {
    return (
      <DownloadPanel
        name={name}
        url={url}
        icon="📄"
        label="File too large to preview — download to view"
      />
    )
  }

  if (error) {
    return (
      <DownloadPanel name={name} url={url} icon="📄" label={error} />
    )
  }

  return (
    <div className="pm-dr-preview" style={{ padding: 16 }}>
      <pre style={{
        margin: 0, padding: 12, fontSize: 11, lineHeight: 1.5,
        background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 4,
        maxHeight: 480, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {text}
      </pre>
    </div>
  )
}

function PdfPreview({ url, name }: { url: string; name: string }) {
  return (
    <div
      className="pm-dr-preview"
      style={{
        background: 'var(--bg)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r)',
        padding: '12px',
      }}
    >
      <iframe
        src={url}
        title={name}
        style={{
          width: '100%', height: 480, border: '1px solid var(--line)',
          borderRadius: 'var(--rs)', background: '#fff', display: 'block',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>{name}</div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn b-ol"
          style={{ padding: '6px 14px', textDecoration: 'none', fontSize: 11 }}
        >
          Open full size
        </a>
      </div>
    </div>
  )
}

interface Props {
  projectId: number
  item:      HubInboxItem
}

export default function DocReviewPreview({ projectId, item }: Props) {
  const url  = downloadUrl(projectId, item.id)
  const kind = fileKind(item.original_name, item.mime_type)
  const name = item.original_name

  if (kind === 'pdf') {
    return <PdfPreview url={url} name={name} />
  }

  if (kind === 'image') {
    return (
      <div className="pm-dr-preview" style={{ textAlign: 'center', padding: 16 }}>
        <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: 480, borderRadius: 4 }} />
      </div>
    )
  }

  if (kind === 'excel') {
    return <DownloadPanel name={name} url={url} icon="📊" label="Excel file — download to view" />
  }

  if (kind === 'word') {
    return <DownloadPanel name={name} url={url} icon="📝" label="Word document — download to view" />
  }

  if (kind === 'text') {
    return <TextPreview url={url} name={name} />
  }

  return <DownloadPanel name={name} url={url} icon="📄" />
}
